import CryptoJS from 'crypto-js';

/**
 * Utility to generate trigger codes for journey testing
 * Generates a deterministic 6-digit code based on flowId using SHA-256
 * This EXACTLY matches the backend Python implementation in FlowCodeUtils:
 * 
 * Python implementation:
 * ```python
 * n = int(hashlib.sha256(flow_id.encode()).hexdigest(), 16)
 * return f"#{n % (10 ** length):0{length}d}"
 * ```
 */

const DEFAULT_CODE_LENGTH = 6;

/**
 * Generate a short hash code from a flow_id using SHA-256
 * Matches the backend Python implementation exactly
 * 
 * @param flowId - The flow ID to encode (e.g., "abc123")
 * @param length - Length of the numeric part (default: 6)
 * @returns A hash code WITHOUT # prefix (e.g., "691793")
 * 
 * Example:
 * >>> generateTriggerCode("abc123")
 * "691793"
 */
export const generateTriggerCode = (flowId: string, length: number = DEFAULT_CODE_LENGTH): string => {
  if (!flowId || typeof flowId !== 'string') {
    return "0".repeat(length);
  }

  // Step 1: Generate SHA-256 hash (equivalent to hashlib.sha256(flow_id.encode()))
  const hash = CryptoJS.SHA256(flowId);
  
  // Step 2: Convert to hex string (equivalent to .hexdigest())
  const hashHex = hash.toString(CryptoJS.enc.Hex);
  
  // Step 3: Convert hex to BigInt (equivalent to int(hexdigest, 16))
  // Use BigInt to handle large numbers (Python int can handle arbitrary precision)
  const n = BigInt('0x' + hashHex);
  
  // Step 4: Take modulo 10^length (equivalent to n % (10 ** length))
  const modulo = BigInt(10 ** length);
  const result = n % modulo;
  
  // Step 5: Format with leading zeros (equivalent to :0{length}d)
  const code = result.toString().padStart(length, '0');
  
  return code;
};

/**
 * Format trigger code with hash prefix for display
 * 
 * @param code - The trigger code (without #)
 * @returns Formatted code with # prefix (e.g., "#123456")
 */
export const formatTriggerCode = (code: string): string => {
  return `#${code}`;
};
